<template>
  <HrPageLayout
    title="Monthly Detailed Report"
    subtitle="Monthly Detailed Clock-in Report"
    icon="📊"
    :breadcrumb-items="[
      { label: 'Home', route: '/' },
      { label: 'Leave & Attendance' },
      { label: 'Monthly Clock-in Report' }
    ]"
  >
    <template #header-actions>
      <button
        v-if="hasDownloadPermission"
        type="button"
        class="hr-btn hr-btn-success"
        @click="downloadMonthlyReport"
        :disabled="downloadLoading"
      >
        <i class="bx bx-file hr-me-1"></i>
        {{ downloadLoading ? 'Generating…' : 'Download Excel' }}
      </button>
      <span v-else class="hr-text-muted hr-small">Excel: no permission</span>
    </template>

    <div class="attendance-detailed-content">
      <div class="hr-alert hr-alert-info hr-mb-4">
        <i class="bx bx-calendar hr-me-2"></i>
        <div>
          <strong>Report Period:</strong> {{ fromDate }} to {{ toDate }}
        </div>
      </div>

      <div class="hr-chart-card hr-mb-4">
        <div class="hr-chart-body">
          <div class="hr-form-grid">
            <div class="hr-filter-group">
              <label class="hr-form-label">From Date</label>
              <input type="date" v-model="fromDate" class="hr-input" @change="getReportData">
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">To Date</label>
              <input type="date" v-model="toDate" class="hr-input" @change="getReportData">
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">Department</label>
              <select v-model="selectedDepartment" class="hr-select" @change="getReportData">
                <option value="">All Departments</option>
                <option v-for="dept in departments" :key="dept.id" :value="dept.id">{{ dept.departmentName }}</option>
              </select>
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">Search</label>
              <div class="hr-search-wrap">
                <input type="text" v-model="searchValue" class="hr-input" placeholder="Search employee..." @input="onSearchInput">
                <i class="bx bx-search-alt search-icon"></i>
              </div>
            </div>
            <div class="hr-filter-group hr-flex hr-align-end">
              <button type="button" class="hr-btn hr-btn-primary" @click="getReportData">
                <i class="bx bx-refresh"></i> Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Sleek Tabs -->
      <div class="hr-tabs hr-mb-4" style="border-bottom: 1px solid #e2e8f0; display: flex; gap: 0.5rem;">
        <button type="button" @click="activeTab = 'data'" style="padding: 0.5rem 1rem; font-size: 0.875rem; background: transparent; border: none; border-bottom: 2px solid transparent; cursor: pointer; font-weight: 600; transition: all 0.2s;" :style="activeTab === 'data' ? 'border-bottom-color: #3b82f6; color: #3b82f6;' : 'color: #64748b;'">
          <i class="bx bx-table hr-me-1"></i> Data View
        </button>
        <button type="button" @click="activeTab = 'summary'" style="padding: 0.5rem 1rem; font-size: 0.875rem; background: transparent; border: none; border-bottom: 2px solid transparent; cursor: pointer; font-weight: 600; transition: all 0.2s;" :style="activeTab === 'summary' ? 'border-bottom-color: #3b82f6; color: #3b82f6;' : 'color: #64748b;'">
          <i class="bx bx-pie-chart-alt-2 hr-me-1"></i> Summary & Metrics
        </button>
      </div>

      <!-- Summary Tab -->
      <div v-show="activeTab === 'summary'">
        <div class="hr-metrics-grid hr-mb-4">
          <div class="hr-metric-card gradient-1">
            <div class="hr-metric-icon"><i class="bx bx-group"></i></div>
            <p class="hr-metric-label">Total Employees</p>
            <h2 class="hr-metric-value">{{ summary.totalEmployees }}</h2>
          </div>
          <div class="hr-metric-card gradient-2">
            <div class="hr-metric-icon"><i class="bx bx-calendar-check"></i></div>
            <p class="hr-metric-label">Days Covered</p>
            <h2 class="hr-metric-value">{{ summary.daysCovered }}</h2>
          </div>
          <div class="hr-metric-card gradient-3">
            <div class="hr-metric-icon"><i class="bx bx-time-five"></i></div>
            <p class="hr-metric-label">Total Clock-ins</p>
            <h2 class="hr-metric-value">{{ summary.totalClockIns }}</h2>
          </div>
        </div>
      </div> <!-- End Summary Tab -->

      <!-- Data Tab -->
      <div v-show="activeTab === 'data'">
      <div class="hr-chart-card">
        <div class="hr-chart-body">
          <div v-if="loading" class="hr-loading-wrap">
            <div class="hr-spinner"></div>
            <p>Generating report...</p>
          </div>
          <div v-else-if="reportData.length === 0" class="hr-empty-state">
            <div class="hr-empty-icon"><i class="bx bx-file"></i></div>
            <h5>No report data in this view</h5>
            <p class="hr-text-muted">Try adjusting your date range or filters, or use <strong>Download Excel</strong> above to generate a report from stored attendance data.</p>
          </div>
          <div v-else class="hr-table-wrapper">
            <table class="hr-table hr-table-bordered">
              <thead>
                <tr>
                  <th>Payroll No</th>
                  <th>Name</th>
                  <th>Date</th>
                  <th>Arrived At Work</th>
                  <th>Out For Lunch</th>
                  <th>Back From Lunch</th>
                  <th>Leaving Work</th>
                  <th>Remote Check-in</th>
                  <th>Remote Check-out</th>
                  <th>Tasks</th>
                  <th>Project Code</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in reportData" :key="index">
                  <td>{{ row.payrollNo }}</td>
                  <td>{{ row.name }}</td>
                  <td>{{ formatDate(row.date) }}</td>
                  <td>{{ formatTime(row.arrivedAtWork) }}</td>
                  <td>{{ formatTime(row.outForLunch) }}</td>
                  <td>{{ formatTime(row.backFromLunch) }}</td>
                  <td>{{ formatTime(row.leavingWork) }}</td>
                  <td>
                    <a v-if="row.clockInLatitude || row.clockInLat" :href="`https://maps.google.com/?q=${row.clockInLatitude || row.clockInLat},${row.clockInLongitude || row.clockInLong}`" target="_blank" class="hr-link hr-small">
                      <i class="bx bx-map"></i> View Map
                    </a>
                    <span v-else class="hr-text-muted">-</span>
                  </td>
                  <td>
                    <a v-if="row.clockOutLatitude || row.clockOutLat" :href="`https://maps.google.com/?q=${row.clockOutLatitude || row.clockOutLat},${row.clockOutLongitude || row.clockOutLong}`" target="_blank" class="hr-link hr-small">
                      <i class="bx bx-map"></i> View Map
                    </a>
                    <span v-else class="hr-text-muted">-</span>
                  </td>
                  <td class="tasks">{{ row.tasks }}</td>
                  <td>{{ row.projectCode }}</td>
                </tr>
              </tbody>
            </table>

          </div>



          <div class="hr-pagination-wrap hr-flex-between hr-mt-4" v-if="totalPages > 1">
            <div class="hr-text-muted hr-small">Showing {{ startIndex + 1 }} to {{ Math.min(endIndex, totalItems) }} of {{ totalItems }} entries</div>
            <nav class="hr-pagination">
              <button type="button" class="hr-page-btn" :disabled="currentPage <= 1" @click="changePage(currentPage - 1)"><i class="bx bx-chevron-left"></i></button>
              <template v-for="(page, idx) in visiblePages" :key="idx">
                <button v-if="page !== '...'" type="button" class="hr-page-btn" :class="{ active: page === currentPage }" @click="changePage(page)">{{ page }}</button>
                <span v-else class="hr-page-ellipsis">...</span>
              </template>
              <button type="button" class="hr-page-btn" :disabled="currentPage >= totalPages" @click="changePage(currentPage + 1)"><i class="bx bx-chevron-right"></i></button>
            </nav>
          </div>
        </div>
      </div>
      </div> <!-- End Data Tab -->
    </div>
  </HrPageLayout>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute, executeDownload, formatMobileKe, getRight } from "@/api";
import _ from 'lodash';
import * as XLSX from 'xlsx';

export default {
  name: "AttendanceDetailedReport",
  components: { HrPageLayout },
  data() {
    // Default to current month so report is more likely to show data
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    const formatDateInput = (date) => {
      return date.toISOString().split('T')[0];
    };

    return {
      activeTab: 'data',
      // Report data
      reportData: [],
      
      // Date range (current month by default)
      fromDate: formatDateInput(firstDay),
      toDate: formatDateInput(lastDay),
      
      // Filters
      departments: [],
      selectedDepartment: "",
      searchValue: "",
      
      // Pagination
      currentPage: 1,
      pageSize: 20,
      totalPages: 1,
      totalItems: 0,
      
      // Loading state
      loading: false,
      downloadLoading: false,
      
      // Summary stats
      summary: {
        totalEmployees: 0,
        daysCovered: 0,
        totalClockIns: 0,
        totalRecords: 0
      },
      
      // Permission check - using DETAILED MONTHLY ATTENDANCE role
      hasDownloadPermission: false,
      
      // Sample data (for demonstration)
      sampleData: [
        {
          payrollNo: "CSPL0010",
          name: "Nancy Muthoni Mbugua",
          email: "nancy.muthoni@craftsilicon.com",
          mobile: "",
          branch: "",
          department: "",
          date: "2025-09-01",
          arrivedAtWork: "06:58:41",
          outForLunch: "",
          backFromLunch: "",
          leavingWork: "16:37:21",
          tasks: "ARRIVED AT WORK : 06:58:41\nLEFT WORK : 16:37:21",
          projectCode: ""
        },
        {
          payrollNo: "CSPL0011",
          name: "John Doe",
          email: "john.doe@craftsilicon.com",
          mobile: "0712345678",
          branch: "Nairobi",
          department: "IT",
          date: "2025-09-01",
          arrivedAtWork: "08:30:00",
          outForLunch: "13:00:00",
          backFromLunch: "14:00:00",
          leavingWork: "17:30:00",
          tasks: "Software Development",
          projectCode: "PRJ001"
        },
        {
          payrollNo: "CSPL0012",
          name: "Jane Smith",
          email: "jane.smith@craftsilicon.com",
          mobile: "0723456789",
          branch: "Mombasa",
          department: "Finance",
          date: "2025-09-01",
          arrivedAtWork: "07:45:00",
          outForLunch: "12:30:00",
          backFromLunch: "13:30:00",
          leavingWork: "16:45:00",
          tasks: "Financial Reporting",
          projectCode: "PRJ002"
        }
      ]
    };
  },
  computed: {
    startIndex() {
      return (this.currentPage - 1) * this.pageSize;
    },
    endIndex() {
      return this.currentPage * this.pageSize;
    },
    visiblePages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);

      if (end - start < maxVisible - 1) {
        start = Math.max(1, end - maxVisible + 1);
      }

      if (start > 1) {
        pages.push(1);
        if (start > 2) pages.push('...');
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (end < this.totalPages) {
        if (end < this.totalPages - 1) pages.push('...');
        pages.push(this.totalPages);
      }

      return pages;
    },
    paginatedData() {
      return this.reportData.slice(this.startIndex, this.endIndex);
    }
  },
  mounted() {
    this.checkDownloadPermission();
    this.getDepartments();
    this.getReportData();
  },
  methods: {
    formatMobileKe,
    // Format date for display
    formatDate(dateString) {
      if (!dateString) return '-';
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      } catch (e) {
        return dateString;
      }
    },

    // Format time for display
    formatCoordinates(lat, lng) {
      if (lat == null && lng == null) return '-';
      const a = parseFloat(lat);
      const b = parseFloat(lng);
      if (Number.isNaN(a) || Number.isNaN(b)) return '-';
      return `${a}, ${b}`;
    },
    formatTime(timeString) {
      if (!timeString || timeString.trim() === '') return '-';
      
      // If it's already in HH:MM:SS format, just return it
      if (/^\d{2}:\d{2}:\d{2}$/.test(timeString)) {
        return timeString;
      }
      
      // Try to parse time
      try {
        const [hours, minutes, seconds] = timeString.split(':');
        return `${hours}:${minutes}:${seconds}`;
      } catch (e) {
        return timeString;
      }
    },

    // Get departments
    getDepartments() {
      const data = new FormData();
      data.append("function", "getDepartments");
      execute(data, authUrl)
        .then((res) => {
          if (res.data.success && res.data.data?.departments) {
            this.departments = res.data.data.departments;
          }
        })
        .catch((e) => {
          console.error("Error fetching departments:", e);
          this.showToast("Failed to load departments", "error");
        });
    },

    // Get report data
    getReportData() {
      this.loading = true;
      
      const data = new FormData();
      data.append("function", "getMonthlyClockInReport");
      data.append("fromDate", this.fromDate);
      data.append("toDate", this.toDate);
      data.append("departmentId", this.selectedDepartment);
      data.append("search", this.searchValue);
      data.append("page", this.currentPage);
      data.append("rows_per_page", this.pageSize);

      execute(data, authUrl)
        .then((res) => {
          if (res.data.success) {
            this.reportData = res.data.data.report || [];
            this.totalItems = res.data.data.totalCount || 0;
            this.totalPages = Math.ceil(this.totalItems / this.pageSize);
            this.summary = res.data.data.summary || {
              totalEmployees: res.data.data.totalEmployees || 0,
              daysCovered: res.data.data.daysCovered || 0,
              totalClockIns: res.data.data.totalClockIns || 0,
              totalRecords: this.totalItems
            };
          } else {
            const errorMsg = res.data.message || res.data.error || "Failed to load report data";
            console.error("Error:", errorMsg, res.data);
            this.showToast(errorMsg, "error");
            // Fallback to empty data
            this.reportData = [];
            this.totalItems = 0;
            this.totalPages = 1;
          }
          this.loading = false;
        })
        .catch((e) => {
          console.error("API error:", e);
          this.showToast("Network error while loading report data", "error");
          this.loading = false;
          // Fallback to empty data
          this.reportData = [];
          this.totalItems = 0;
          this.totalPages = 1;
        });
    },

    // Handle search input with debounce
    onSearchInput: _.debounce(function() {
      this.currentPage = 1;
      this.getReportData();
    }, 500),

    // Change page
    changePage(page) {
      if (page < 1 || page > this.totalPages || page === this.currentPage) {
        return;
      }
      this.currentPage = page;
      this.getReportData();
    },

    // Download permission: MONTHLY ATTENDANCE or DETAILED MONTHLY ATTENDANCE
    checkDownloadPermission() {
      this.hasDownloadPermission = getRight('DETAILED MONTHLY ATTENDANCE') || getRight('MONTHLY ATTENDANCE');
    },
    
    // Download Excel from backend (uses attendance table; works even when on-screen table is empty)
    async downloadMonthlyReport() {
      if (!this.hasDownloadPermission) {
        this.showToast("You don't have permission to download this report", "error");
        return;
      }

      this.downloadLoading = true;
      try {
        const data = new FormData();
        data.append("function", "downloadMonthlyClockInReport");
        data.append("fromDate", this.fromDate);
        data.append("toDate", this.toDate);
        data.append("search", this.searchValue || "");
        data.append("departmentId", this.selectedDepartment || "");

        const response = await executeDownload(data, authUrl);
        const blob = new Blob([response.data], {
          type: response.headers["content-type"] || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        });

        if (blob.size === 0) {
          this.showToast("Report returned no data", "warning");
          return;
        }

        const contentType = (response.headers["content-type"] || "").toLowerCase();
        if (contentType.indexOf("json") !== -1) {
          const text = await blob.text();
          try {
            const json = JSON.parse(text);
            const msg = json.message || json.error || "Download failed";
            this.showToast(msg, "error");
            return;
          } catch {
            void 0; // non-JSON or parse error; blob is not our error payload
          }
        }

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `Monthly_ClockIn_Report_${this.fromDate}_to_${this.toDate}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.showToast("Excel report downloaded", "success");
      } catch (error) {
        console.error("Error downloading report:", error);
        let msg = "Failed to download report";
        if (error.response?.data instanceof Blob) {
          try {
            const t = await error.response.data.text();
            const j = JSON.parse(t);
            if (j.message || j.error) msg = j.message || j.error;
          } catch {
            void 0; // use generic msg if blob parse fails
          }
        } else if (error.response?.data?.message) {
          msg = error.response.data.message;
        } else if (error.message) {
          msg = error.message;
        }
        this.showToast(msg, "error");
      } finally {
        this.downloadLoading = false;
      }
    },
    
    // Export to Excel (legacy method - kept for backward compatibility)
    exportToExcel() {
      if (this.reportData.length === 0) {
        this.showToast("No data to export", "warning");
        return;
      }

      try {
        const workbook = XLSX.utils.book_new();

        // Create headers (no email, mobile, branch, department)
        const headers = [
          'Payroll No',
          'Name',
          'Date',
          'Arrived At Work',
          'Out For Lunch',
          'Back From Lunch',
          'Leaving Work',
          'Remote Clock-in Coordinates',
          'Remote Clock-out Coordinates',
          'Tasks',
          'Project Code'
        ];

        // Create data
        const excelData = [];
        excelData.push(headers);

        this.reportData.forEach(row => {
          excelData.push([
            row.payrollNo,
            row.name,
            this.formatDate(row.date),
            row.arrivedAtWork,
            row.outForLunch,
            row.backFromLunch,
            row.leavingWork,
            this.formatCoordinates(row.clockInLatitude || row.clockInLat, row.clockInLongitude || row.clockInLong),
            this.formatCoordinates(row.clockOutLatitude || row.clockOutLat, row.clockOutLongitude || row.clockOutLong),
            row.tasks,
            row.projectCode
          ]);
        });

        // Add summary sheet
        const summaryData = [
          ['MONTHLY CLOCK-IN REPORT'],
          ['Report Period', `${this.fromDate} to ${this.toDate}`],
          ['Generated On', new Date().toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })],
          [],
          ['Summary Statistics'],
          ['Total Employees', this.summary.totalEmployees],
          ['Days Covered', this.summary.daysCovered],
          ['Total Clock-ins', this.summary.totalClockIns],
          ['Total Records', this.summary.totalRecords]
        ];

        const reportSheet = XLSX.utils.aoa_to_sheet(excelData);
        const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);

        XLSX.utils.book_append_sheet(workbook, reportSheet, 'Clock-in Report');
        XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

        const fileName = `Monthly_ClockIn_Report_${this.fromDate}_to_${this.toDate}.xlsx`;
        XLSX.writeFile(workbook, fileName);

        this.showToast("Excel report downloaded successfully", "success");
      } catch (error) {
        console.error("Error generating Excel file:", error);
        this.showToast("Failed to generate Excel report", "error");
      }
    },

    // Show toast notification
    showToast(message, type = 'info') {
      if (this.$toast) {
        this.$toast[type](message);
      } else {
        // Fallback for when toast is not available
        console.log(`${type.toUpperCase()}: ${message}`);
        alert(message);
      }
    }
  },
  watch: {
    fromDate() {
      this.currentPage = 1;
      // Optional: Add validation to ensure fromDate <= toDate
      if (this.fromDate > this.toDate) {
        this.toDate = this.fromDate;
      }
    },
    toDate() {
      this.currentPage = 1;
      // Optional: Add validation to ensure fromDate <= toDate
      if (this.toDate < this.fromDate) {
        this.fromDate = this.toDate;
      }
    }
  }
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";

.attendance-detailed-content { padding: 0; }
.hr-align-end { align-items: flex-end; }
.hr-page-ellipsis { display: inline-flex; align-items: center; min-width: 36px; padding: 0 4px; color: #64748b; font-size: 14px; }
.search-icon { position: absolute; right: 10px; top: 50%; transform: translateY(-50%); color: #74788d; }

.card {
  border-radius: 0.5rem;
  border: none;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  transition: all 0.3s ease;
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

.avatar-sm {
  width: 2.5rem;
  height: 2.5rem;
}

.avatar-title {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-weight: 500;
}

.table {
  font-size: 0.875rem;
}

.table th {
  background-color: #f8f9fa;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.75rem;
  border-bottom: 2px solid #dee2e6;
}

.table td {
  vertical-align: middle;
  border: 1px solid #dee2e6;
}

.tasks {
  white-space: pre-line;
  font-size: 0.8rem;
  max-width: 200px;
}

.pagination-rounded .page-link {
  border-radius: 50%;
  margin: 0 3px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.page-item.active .page-link {
  background-color: #556ee6;
  border-color: #556ee6;
}

.page-link {
  color: #556ee6;
}

.page-link:hover {
  color: #2b3a92;
  background-color: #f0f0f8;
}

.font-size-14 {
  font-size: 0.875rem !important;
}

.font-size-18 {
  font-size: 1.125rem !important;
}

.font-size-48 {
  font-size: 3rem !important;
}

.bg-light {
  background-color: #f8f9fa !important;
}

@media (max-width: 768px) {
  .table {
    font-size: 0.75rem;
  }
  
  .table th,
  .table td {
    padding: 0.5rem;
  }
  
  .card-header .row {
    row-gap: 1rem;
  }
}

/* Loading spinner animation */
.spinner-border {
  animation: spinner-border 0.75s linear infinite;
}

@keyframes spinner-border {
  to { transform: rotate(360deg); }
}
</style>